import { isHttpError } from '@/http';
import { redactUrlSecrets } from '@/utils/redact-url-secrets';

declare global {
	interface Window {
		gtag?: (...args: unknown[]) => void;
	}
}

// Event params must only carry enums and booleans: never send PII.
export type AnalyticsParams = Record<string, string | boolean>;

const measurementId: string | undefined = import.meta.env
	.VITE_GA_MEASUREMENT_ID;

// GA user_id must be an opaque ID. The backend user ID is expected to be a
// Cognito UUID; anything else (e.g. an email) is dropped instead of sent.
const OPAQUE_ID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function toOpaqueUserId(userId: string | null): string | null {
	return userId && OPAQUE_ID_PATTERN.test(userId) ? userId : null;
}

export type ErrorReason =
	| 'network_error'
	| 'http_4xx'
	| 'http_5xx'
	| 'unexpected_error';

// Classifies a caught error without sending its message, URL or status text.
// HttpError has no status when fetch itself failed (CORS, offline, backend down).
export function getErrorReason(error: unknown): ErrorReason {
	if (!isHttpError(error)) return 'unexpected_error';
	if (error.status === undefined) return 'network_error';
	if (error.status >= 500) return 'http_5xx';
	if (error.status >= 400) return 'http_4xx';
	return 'unexpected_error';
}

function gtag(...args: unknown[]) {
	if (typeof window.gtag === 'function') {
		window.gtag(...args);
	}
}

export function initAnalytics() {
	if (!measurementId) return;
	const userId = toOpaqueUserId(localStorage.getItem('user-id'));
	// This config call sends the first page_view, whose page_location defaults
	// to the current URL. The public lineage route carries an access token in
	// `?k=`, so the location is redacted before GA ever sees it.
	gtag('config', measurementId, {
		...(userId ? { user_id: userId } : {}),
		page_location: redactUrlSecrets(window.location.href),
	});
}

export function trackEvent(name: string, params?: AnalyticsParams) {
	if (!measurementId) {
		console.debug('[analytics]', name, params ?? {});
		return;
	}
	// GA4 only: without send_to, gtag also forwards events to the Ads tag.
	gtag('event', name, { ...params, send_to: measurementId });
}

export function setAnalyticsUser(userId: string | null) {
	if (!measurementId) return;
	gtag('config', measurementId, {
		user_id: toOpaqueUserId(userId),
		send_page_view: false,
	});
}
